import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { GrCircleInformation } from "react-icons/gr";
import { useDispatch, useSelector } from "react-redux";
import ButtonComponent from "../../../../../../../../app/common-components/ButtonComponent";
import CustomModal from "../../../../../../../../app/common-components/CustomModal";
import DiscardChanges from "../../../../../../../../app/common-components/DiscardChanges";
import Input from "../../../../../../../../app/common-components/form/input";
import Spinners from "../../../../../../../../app/common-components/Spinner";
import CompleteTableData from "../../../../../../../../app/common-components/Table/CompleteTableData";
import { getSortingValues } from "../../../../../../../../app/Constants";
import { useCommonAlert } from "../../../../../../../../app/utilities/helpers";
import {
  deleteTypeVulnerabilityConfig,
  PriorityNameList,
  TypeConfigSaveUpdate,
  TypeConfiguration,
  TypeNameList,
  UpdatePriorityNameList,
  VMTypeConfigHistory,
} from "../../../../ToolsSlice";
import { VulnerabilityPriorityModal } from "../../../PriorityModal/VulnerabilityPriorityModal";
import DragDropConfigModal from "../../../DragDropConfigModal/DragDropConfigModal";
import "../../../../styles.scss";


const TypeConfigurationTable = () => {
  const dispatch = useDispatch();
  const { callCommonAlert, hideCommonAlert } = useCommonAlert();
  const { saveSeveritySLAConfigLoading, deleteTypeVulnerabilityConfigLoading, TypeNameListData, TypeConfigSaveUpdateLoading, PriorityNameListData, PriorityNameListLoading
  } =
    useSelector((state) => state.toolsApi);
  const hasChangesRef = useRef(false);
  const [state, setState] = useState({
    loading: true,
    tableData: [],
    totalCount: 0,
    addBodyOpenInfo: false,
    addBodyOpenAdd: false,
    addPriorityModalAdd: false,
    addBodyOpenDelete: false,
    showDiscardModal: false,
    pageNo: 0,
    pageLimit: 10,
    searchKey: "",
    sortByColumn: "",
    sortingOrder: "",
    appCriticalityOption: [
      { label: "CAT-1", value: "CAT-1" },
      { label: "CAT-2", value: "CAT-2" },
      { label: "CAT-3", value: "CAT-3" },
      { label: "CAT-4", value: "CAT-4" },
    ],
  });
  const getPayload = ({
    key = "",
    sortByColumn = "",
    sortOrder = "",
  } = {}) => ({
    keyword: key,
    sortByColumn: sortByColumn,
    sortOrder: sortOrder,
  });

  useEffect(() => {
    loadData(0, 10, getPayload());
  }, []);

  const columns = useMemo(() => [
    {
      Header: "Priority",
      accessor: "priority",
      tdClassName: "text-center",
      Cell: ({ value }) => value || value === 0 ? value : "-"
    },
    {
      Header: "Vulnerability Type",
      accessor: "vulnerabilityType",
      className: "text-start",
      tdClassName: "text-center",
    },
    {
      Header: "Plugin Family",
      accessor: "pluginFamilies",
      tdClassName: "text-start",
      thHeaderStyle: {
        width: "600px",
        maxWidth: "600px",
        minWidth: "600px",
        padding: "10px 12px",
        textAlign: "center",
      },
      tdHeaderStyle: {
        width: "600px",
        maxWidth: "600px",
        minWidth: "600px",
        whiteSpace: "normal",
        wordWrap: "break-word",
        overflowWrap: "break-word",
        padding: "10px 12px",
        textAlign: "left",
      },
      Cell: ({ value }) => (
        <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
          {value && Array.isArray(value) && value.length > 0
            ? value.join(", ")
            : "-"}
        </div>
      ),
    },
    {
      Header: "Keywords for Plugin Name",
      accessor: "pluginNameKeywords",
      tdClassName: "text-center",
      Cell: ({ value }) => (
        <>
          {value && Array.isArray(value) && value.length > 0
            ? value.join(", ")
            : "-"}
        </>
      ),
    },
    {
      Header: "Actions",
      accessor: "Action",
      thClassName: "actions",
      tdClassName: "text-center",
      disableSortBy: true,
      Cell: (row) => {
        const isNoPatch = row?.row?.original?.vulnerabilityType === "No Patch";
        return (
          <div className="action-table">
            <div>
              <FiEdit className={`iconStyleActions action-icon${isNoPatch ? ' action-icon--locked' : ''}`} title="Edit"
                onClick={!isNoPatch ? () => addAddBodyOpen(row?.row?.original) : undefined}
                data-testid="editButton" />
            </div>
            <div>
              <FiTrash2 className={`iconStyleActions action-icon${isNoPatch ? ' action-icon--locked' : ''}`} title="Delete"
                onClick={!isNoPatch ? () => deleteBodyOpen(row?.row?.original) : undefined}
                data-testid="deleteButton" />
            </div>
            <div >
              <FaInfoCircle className={`iconStyleActions action-icon${isNoPatch ? ' action-icon--locked' : ''}`}
                onClick={() => addInfoBodyOpen(row?.row?.original)}
                data-testid="infoButton" title="Info" />
            </div>
          </div>
        );
      },
    },
  ], []);
  const loadData = async (pageNumber, itemPerPage, payload) => {
    setState((s) => ({ ...s, loading: true }));

    try {
      const response = await dispatch(
        TypeConfiguration({
          pageNumber: pageNumber,
          itemsPerPage: itemPerPage,
          payload: payload,
        })
      ).unwrap();

      setState((s) => ({
        ...s,
        tableData: response.data || [],
        totalCount: response.totalCount || 0,
      }));
    } catch (err) {
      console.error("API failed:", err);
      // Optional: store an error message
      setState((s) => ({ ...s, error: err }));
    } finally {
      // Always runs — even on 500, 404, 400, or an unexpected cosmic sneeze
      setState((s) => ({ ...s, loading: false }));
    }
  };
  const handlePageNoChange = (pageNo) => {
    setState((prev) => ({
      ...prev,
      pageNo: pageNo,
    }));
    loadData(pageNo, state.pageLimit, getPayload());
  };
  const handlePageLimitChange = (pageLimit) => {
    setState((prev) => ({
      ...prev,
      pageLimit: pageLimit,
    }));
    loadData(state.pageNo, pageLimit, getPayload());
  };
  const onSearch = (searchKey) => {
    setState((prev) => ({
      ...prev,
      searchKey: searchKey,
    }));
    loadData(state.pageNo, state.pageLimit, getPayload({ key: searchKey }));
  };

  const handleOnSorting = (column, headerSortingState) => {
    const { sortingField, isDataSorted } = getSortingValues(
      columns,
      column,
      headerSortingState
    );
    setState((prev) => ({
      ...prev,
      sortByColumn: sortingField,
      sortingOrder: isDataSorted,
    }));
    loadData(
      state.pageNo,
      state.pageLimit,
      getPayload({
        key: state.searchKey,
        sortByColumn: sortingField,
        sortOrder: isDataSorted,
      })
    );
  };
  const deleteTableData = (id) => {
    dispatch(deleteTypeVulnerabilityConfig(id)).then((data) => {
      if (data?.payload?.status === 200) {
        callCommonAlert("success", data.payload.message);
        hideCommonAlert();
        addBodyClose();
        setState((prev) => ({
          ...prev,
          pageNo: 0,
          pageLimit: 10,
          searchKey: "",
        }));
        loadData(0, 10, getPayload());
      } else {
        callCommonAlert("error", "Severity SLA Configuration Failed to save");
        hideCommonAlert();
      }
      setState((prev) => ({
        ...prev,
        pageNo: 0,
        pageLimit: 10,
        searchKey: "",
      }));
      loadData(0, 10, getPayload());
      deleteBodyClose();
    });
  };

  const { loading, tableData, totalCount } = state;

  // Reusable input with "Days" label
  const FieldWithDays = ({ label, name, value, onChange, error }) => {
    return (
      <div className="d-flex align-items-center w-100">
        <span className="w-50 me-2">
          <Input
            label={label}
            type="number"
            name={name}
            id={name}
            value={value || ""}
            error={error}
            errorMessage={
              error ? (
                <div className="informationIconStyle">
                  <div className="AppConfigTooltip">
                    <GrCircleInformation />
                    <span className="AppConfigtooltiptext p-1">
                      {`${label} should not be empty`}
                    </span>
                  </div>
                </div>
              ) : (
                ""
              )
            }
            containerClassName="frmgroup default-input"
            onChange={(e) => onChange(name, e.target.value)}
          />
        </span>
        <span> Days</span>
      </div>
    );
  };


  const handleTypeConfigSave = (saveData) => {
    console.log(saveData);
    // Prepare payload based on whether it's add or edit
    const payload = saveData.id && saveData.id !== 0
      ? {
        id: saveData.id,
        vulnerabilityType: saveData.vulnerabilityTypes,
        pluginFamilies: saveData.pluginFamilies,
        pluginNameKeywords: saveData.pluginNameKeywords
      }
      : {
        vulnerabilityType: saveData.vulnerabilityTypes,
        pluginFamilies: saveData.pluginFamilies,
        pluginNameKeywords: saveData.pluginNameKeywords
      };

    dispatch(TypeConfigSaveUpdate(payload)).then((data) => {
      if (data?.payload?.status === 200) {
        callCommonAlert("success", data.payload.message);
        hideCommonAlert();
        addBodyClose();
        loadData(0, 10, getPayload());
      } else if (data?.payload?.status === 205) {
        callCommonAlert("error", data.payload.message);
        hideCommonAlert();
      } else if (data?.payload?.status === 409) {
        callCommonAlert("warning", data.payload.message);
        hideCommonAlert();
      } else {
        callCommonAlert("error", data.payload.message);
        hideCommonAlert();
      }
    }).catch((error) => {
      console.error("Save error:", error);
      callCommonAlert("error", "Type Configuration Failed to save");
      hideCommonAlert();
    });
  };

  const addAddBodyOpen = (row) => {
    dispatch(TypeNameList());
    setState((prev) => ({
      ...prev,
      addBodyOpenAdd: true,
      modelRowData: row,
    }));
    hasChangesRef.current = false;
  };

  const addPriorityModalOpen = (row) => {

    dispatch(PriorityNameList()).then(() => {
      // Open modal only after data is fetched
      setState((prev) => ({
        ...prev,
        addPriorityModalAdd: true,
        modelRowData: row,
      }));
    });
  };

  const addPriorityModalClose = () => {
    setState((prev) => ({
      ...prev,
      addPriorityModalAdd: false,
      modelRowData: "",
      showDiscardModal: false,
    }));
    hasChangesRef.current = false;
  };

  const handlePrioritySave = (updatedItems) => {
    // updatedItems contains the array with updated Priority values
    const reAlignPriorities = (data) => {
      return data.map((item, index) => {
        return {
          type: item.type,
          priority: `Priority ${index + 1}`
        };
      });
    };

    const priorityPayload = reAlignPriorities(updatedItems); // Rename this variable

    if (priorityPayload) {
      dispatch(UpdatePriorityNameList(priorityPayload)).then((data) => {
        console.log(data?.payload?.status);
        if (Number(data?.payload?.status) === 200) {

          callCommonAlert("success", data.payload.message);
          hideCommonAlert();
          addPriorityModalClose();
          setState((prev) => ({
            ...prev,
            pageNo: 0,
            pageLimit: 10,
            searchKey: "",
          }));
          loadData(0, 10, getPayload()); // Now this calls the correct function
        } else if (data?.payload?.status === 205) {

          callCommonAlert("error", data.payload.message);
          hideCommonAlert();
        } else if (data?.payload?.status === 409) {

          callCommonAlert("warning", data.payload.message);
          hideCommonAlert();
        } else {

          callCommonAlert("error", "Priority Configuration Failed to save");
          hideCommonAlert();
        }
      }).catch((error) => {

        callCommonAlert("error", "Priority Configuration Failed to save");
        hideCommonAlert();
      });
    }
  };


  const addBodyClose = () => {
    setState((prev) => ({
      ...prev,
      addBodyOpenAdd: false,
      modelRowData: "",
      showDiscardModal: false,
    }));
    hasChangesRef.current = false;
  };
  const handleCloseAttempt = () => {
    if (hasChangesRef.current) {
      setState((prev) => ({ ...prev, showDiscardModal: true }));
    } else {
      addBodyClose();
    }
  };
  const handleDiscardConfirm = () => {
    hasChangesRef.current = false;
    addBodyClose();
  };
  const handleDiscardCancel = () => {
    setState((prev) => ({ ...prev, showDiscardModal: false }));
  };
  const discardModalBody = () => (
    <DiscardChanges
      title="edit"
      discardClick={handleDiscardConfirm}
      cancelClick={handleDiscardCancel}
    />
  );

  const addInfoBodyOpen = (row) => {
    setState((prev) => ({
      ...prev,
      addBodyOpenInfo: true,
      modelRowData: row,
      historyData: [],
      historyTotalCount: 0,
      historyPageNo: 0,
      historyPageLimit: 10,
      historySearchKey: "",
      historyLoading: false,
    }));
    loadHistoryData(row?.id, 0, 10, { keyword: "" });
  };

  const loadHistoryData = async (configId, pageNumber, itemsPerPage, payload) => {
    setState((s) => ({ ...s, historyLoading: true }));
    try {
      const response = await dispatch(
        VMTypeConfigHistory({
          slaConfigId: configId,
          pageNumber,
          itemsPerPage,
          payload,
        })
      ).unwrap();
      setState((s) => ({
        ...s,
        historyData: response.data || [],
        historyTotalCount: response.totalCount || 0,
      }));
    } catch (err) {
      console.error("History API failed:", err);
      setState((s) => ({ ...s, historyData: [], historyTotalCount: 0 }));
    } finally {
      setState((s) => ({ ...s, historyLoading: false }));
    }
  };

  const historyColumns = [
    {
      Header: "Updated On",
      accessor: "updatedDt",
      tdClassName: "text-center",
      disableSortBy: true,
      Cell: ({ value }) => <>{value || "-"}</>,
    },
    {
      Header: "Updated By",
      accessor: "updatedBy",
      tdClassName: "text-center",
      disableSortBy: true,
      Cell: ({ value }) => <>{value || "-"}</>,
    },
    {
      Header: "Changes",
      accessor: "message",
      tdClassName: "text-start",
      disableSortBy: true,
      Cell: ({ value }) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {value || "-"}
        </div>
      ),
    },
  ];

  const handleHistoryPageNoChange = (pageNo) => {
    setState((prev) => ({ ...prev, historyPageNo: pageNo }));
    loadHistoryData(state.modelRowData?.id, pageNo, state.historyPageLimit, {
      keyword: state.historySearchKey || "",
    });
  };

  const handleHistoryPageLimitChange = (pageLimit) => {
    setState((prev) => ({ ...prev, historyPageLimit: pageLimit, historyPageNo: 0 }));
    loadHistoryData(state.modelRowData?.id, 0, pageLimit, {
      keyword: state.historySearchKey || "",
    });
  };

  const onHistorySearch = (searchKey) => {
    setState((prev) => ({ ...prev, historySearchKey: searchKey }));
    loadHistoryData(state.modelRowData?.id, state.historyPageNo, state.historyPageLimit, {
      keyword: searchKey,
    });
  };

  const addInfoBody = () => {
    return (
      <div>
        <h4>Information</h4>
        <CompleteTableData
          isLoading={state.historyLoading}
          data={state.historyData || []}
          columns={historyColumns}
          listCount={state.historyTotalCount || 0}
          pageLimit={state.historyPageLimit || 10}
          totalPageCount={Math.ceil((state.historyTotalCount || 0) / (state.historyPageLimit || 10)) || 1}
          curentPageNumber={state.historyPageNo || 0}
          btnData={[]}
          searchKey={state.historySearchKey || ""}
          moveSearchToEnd={true}
          onPageNoChange={handleHistoryPageNoChange}
          onPageLimitChange={handleHistoryPageLimitChange}
          onSearch={onHistorySearch}
        />
      </div>
    );
  };

  const addInfoBodyClose = () => {
    setState((prev) => ({
      ...prev,
      addBodyOpenInfo: false,
      modelRowData: "",
      historyData: [],
      historyTotalCount: 0,
      historyPageNo: 0,
      historyPageLimit: 10,
      historySearchKey: "",
    }));
  };
  const deleteBodyOpen = (row) => {
    setState((prev) => ({
      ...prev,
      addBodyOpenDelete: true,
      modelRowData: row,
    }));
  };

  const addTypeConfigBody = () => (
    <DragDropConfigModal
      availableItems={TypeNameListData}
      isOpen={state.addBodyOpenAdd}
      onClose={addBodyClose}
      onSave={handleTypeConfigSave}
      initialData={state.modelRowData}
      isLoading={TypeConfigSaveUpdateLoading}
      labels={{
        createTitle: 'Create Vulnerability Type Configuration',
        editTitle: 'Edit Vulnerability Type Configuration',
        nameLabel: 'Vulnerability Type',
        namePlaceholder: 'e.g., Database',
        nameError: 'Field should not be empty',
        availableLabel: 'Plugin Families',
        selectedLabel: 'Selected Plugin Families',
        selectedError: 'Field should not be empty',
        availableEmpty: 'All Plugin Families selected',
        selectedEmpty: 'Drag Plugin Families here',
        keywordsLabel: 'Plugin Name Keywords',
      }}
      fieldMap={{
        nameReadKey: 'vulnerabilityType',
        itemsReadKey: 'pluginFamilies',
        keywordsReadKey: 'pluginNameKeywords',
        buildSaveData: (formState) => ({
          vulnerabilityTypes: formState.name,
          pluginFamilies: formState.selectedItems,
          pluginNameKeywords: formState.keywords,
          id: formState.id || 0,
        }),
      }}
      showReason={false}
      showAddIcon={true}
      showLoadingSpinner={true}
    />
  );

  const modalDeleteBody = () => (
    <>
      <h4 className="delete-h"> Delete Type Configuration</h4>
      {deleteTypeVulnerabilityConfigLoading ? (
        <Spinners />
      ) : (
        <div>
          <p>Are you sure you want to delete this Type Configuration?</p>
          <div className="btnswrap btn-dlt">
            <ButtonComponent
              text="Cancel"
              className={"cancelBtnStyle btn btn-primary"}
              onClick={deleteBodyClose}
            />
            <ButtonComponent
              text="Delete"
              className={"btnStyle btn btn-primary"}
              onClick={() => deleteTableData(state?.modelRowData?.id)}
            />
          </div>
        </div>
      )}
    </>
  );
  const deleteBodyClose = (row) => {
    setState((prev) => ({
      ...prev,
      addBodyOpenDelete: false,
      modelRowData: "",
    }));
  };
  const sortByPriority = (data) => {
    // Use .slice() to create a copy so we don't mutate the original array
    return data.slice().sort((a, b) => {
      // 1. Extract digits (\d+) from the strings and convert to numbers
      const numA = parseInt(a.priority.match(/\d+/));
      const numB = parseInt(b.priority.match(/\d+/));

      // 2. Subtract to determine order (Ascending: 1, 2, 3...)
      return numA - numB;
    });
  };
  const getSortedPriorityList = sortByPriority(PriorityNameListData);
  return (
    <>
      <CompleteTableData
        isLoading={loading}
        data={tableData}
        columns={columns}
        TableTitle={"Type Configuration"}
        listCount={totalCount}
        pageLimit={state?.pageLimit}
        totalPageCount={Math.ceil(totalCount / 10) || 1}
        curentPageNumber={state?.pageNo}
        btnData={[
          {
            text: "Add",
            className: "solid-btn",
            disabled: false,
            function: addAddBodyOpen,
          },
          {
            text: "Priority Configuration",
            className: "solid-btn",
            disabled: false,
            function: addPriorityModalOpen,
          },
        ]}
        searchKey={state?.searchKey}
        moveSearchToEnd={true}
        onPageNoChange={handlePageNoChange}
        onPageLimitChange={handlePageLimitChange}
        onSearch={onSearch}
        onSorting={handleOnSorting}
        headerSortingState={{
          accessor: state.sortByColumn,
          isDataSorted: state.sortingOrder,
        }}
      />

      <CustomModal
        modalOpen={state.addBodyOpenAdd}
        modalBody={addTypeConfigBody}
        modalHide={addBodyClose}
        closeBtn={true}
        closeBtnFuc={addBodyClose}
        size="xl"
      />

      <VulnerabilityPriorityModal
        data={getSortedPriorityList}
        isOpen={state.addPriorityModalAdd}
        onClose={addPriorityModalClose}
        onSave={handlePrioritySave}
        lockedType="No Patch"
      />

      <CustomModal
        modalOpen={state.showDiscardModal}
        modalBody={discardModalBody}
        modalHide={handleDiscardCancel}
        closeBtn={false}
        closeBtnFuc={handleDiscardCancel}
      />

      <CustomModal
        modalOpen={state.addBodyOpenInfo}
        modalBody={addInfoBody}
        modalHide={addInfoBodyClose}
        closeBtn={true}
        closeBtnFuc={addInfoBodyClose}
        size="lg"
      />
      <CustomModal
        modalOpen={state.addBodyOpenDelete}
        modalBody={modalDeleteBody}
        modalHide={deleteBodyClose}
        closeBtn={true}
        closeBtnFuc={deleteBodyClose}
        size="md"
      />
    </>
  );
};

export default memo(TypeConfigurationTable);
